/* @jsxImportSource @emotion/react */

import { useEffect, useRef, useState } from 'react'
import { css, keyframes } from '@emotion/react'
import { AnimatePresence, motion } from 'motion/react'

import { SuperSourceIcon } from '@slite/components'
import { SuperSource } from '@slite/search-client'

import { MEDIUM_BREAKPOINT } from '../../utils/breakpoints'

// The home SliteAgent scanner, sized to the hero at runtime.
const TRAIL_HEIGHT = 180
const GRID_CELL = 15
const SCAN_DURATION_S = 7
// Line only moves during the first 60% of a cycle; the rest is an idle break.
const TRAVEL_FRACTION = 0.6

const STICKER_SOURCES = [
  SuperSource.SLACK_CHANNEL,
  SuperSource.GITHUB,
  SuperSource.LINEAR,
  SuperSource.GOOGLE_DRIVE,
  SuperSource.HUBSPOT,
  SuperSource.ATTIO,
  SuperSource.NOTION,
  SuperSource.ZOOM,
]

type ScanSticker = {
  id: string
  source: SuperSource
  side: 'left' | 'right'
  sidePct: number
  topPct: number
  rotate: number
  delay: number
}

const rootCss = css`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`

const stickerCss = css`
  position: absolute;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);

  @media (max-width: ${MEDIUM_BREAKPOINT - 1}px) {
    display: none;
  }
`

function buildScanCss(height: number) {
  const scanLine = keyframes`
    0% {
      transform: translateY(${height}px);
      opacity: 1;
    }
    35% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
    60%,
    100% {
      transform: translateY(-${TRAIL_HEIGHT}px);
      opacity: 0;
    }
  `

  const scanTrail = keyframes`
    0% {
      -webkit-mask-position: 0 ${height}px;
      mask-position: 0 ${height}px;
      opacity: 0.4;
    }
    35% {
      opacity: 0.4;
    }
    50% {
      opacity: 0;
    }
    60%,
    100% {
      -webkit-mask-position: 0 -${TRAIL_HEIGHT}px;
      mask-position: 0 -${TRAIL_HEIGHT}px;
      opacity: 0;
    }
  `

  return {
    lineCss: css`
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      height: 2px;
      background: var(--saturn-orange);
      box-shadow: 0 0 8px 1px var(--saturn-orange);
      animation: ${scanLine} ${SCAN_DURATION_S}s linear infinite;
    `,
    trailCss: css`
      position: absolute;
      inset: 0;
      opacity: 0.4;
      background-image: linear-gradient(
          to right,
          var(--saturn-orange) 1px,
          transparent 1px
        ),
        linear-gradient(to bottom, var(--saturn-orange) 1px, transparent 1px);
      background-size: ${GRID_CELL}px ${GRID_CELL}px;
      -webkit-mask-image: linear-gradient(to bottom, #000 0%, transparent 100%);
      mask-image: linear-gradient(to bottom, #000 0%, transparent 100%);
      -webkit-mask-size: 100% ${TRAIL_HEIGHT}px;
      mask-size: 100% ${TRAIL_HEIGHT}px;
      -webkit-mask-repeat: no-repeat;
      mask-repeat: no-repeat;
      animation: ${scanTrail} ${SCAN_DURATION_S}s linear infinite;
    `,
  }
}

export function HeroScan() {
  const ref = useRef<HTMLDivElement>(null)
  const cycleRef = useRef(0)
  const [height, setHeight] = useState(0)
  const [stickers, setStickers] = useState<ScanSticker[]>([])

  useEffect(() => {
    const element = ref.current
    if (!element) return
    const observer = new ResizeObserver(() => setHeight(element.offsetHeight))
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // One random source per side per pass, popping in as the line crosses it.
  const pickStickers = () => {
    cycleRef.current += 1
    const pool = [...STICKER_SOURCES]
    const pickSource = () =>
      pool.splice(Math.floor(Math.random() * pool.length), 1)[0]
    const makeSticker = (side: ScanSticker['side']): ScanSticker => {
      const topPct = 15 + Math.random() * 55
      const y = (topPct / 100) * height
      return {
        id: `${cycleRef.current}-${side}`,
        source: pickSource(),
        side,
        sidePct: 4 + Math.random() * 9,
        topPct,
        rotate: -10 + Math.random() * 20,
        delay:
          (SCAN_DURATION_S * TRAVEL_FRACTION * (height - y)) /
          (height + TRAIL_HEIGHT),
      }
    }
    setStickers([makeSticker('left'), makeSticker('right')])
  }

  const scan = height > 0 ? buildScanCss(height) : null

  return (
    <div ref={ref} css={rootCss} aria-hidden>
      {scan && (
        <>
          <div css={scan.trailCss} />
          <div
            css={scan.lineCss}
            onAnimationStart={pickStickers}
            onAnimationIteration={pickStickers}
          />
          <AnimatePresence>
            {stickers.map((sticker) => (
              <motion.div
                key={sticker.id}
                css={stickerCss}
                style={{
                  top: `${sticker.topPct}%`,
                  [sticker.side]: `${sticker.sidePct}%`,
                  rotate: `${sticker.rotate}deg`,
                }}
                initial={{ opacity: 0, scale: 0.4 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  transition: {
                    delay: sticker.delay,
                    type: 'spring',
                    stiffness: 380,
                    damping: 22,
                  },
                }}
                exit={{
                  opacity: 0,
                  scale: 0.85,
                  transition: { duration: 0.25 },
                }}
              >
                <SuperSourceIcon sourceType={sticker.source} size={52} />
              </motion.div>
            ))}
          </AnimatePresence>
        </>
      )}
    </div>
  )
}
